import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../api/api';
import { useAuth } from '../../hooks/useAuth';
import { useNotification } from '../../hooks/useNotification';
import GoogleAuthButton from './GoogleAuthButton';
import BrandHomeLink from '../common/BrandHomeLink';
import './AuthPages.css';

export default function OrderTrackingSignup() {
  const { t } = useTranslation('auth');
  const location = useLocation();
  // Arriving here from a login attempt on an unverified account (state.verifyEmail set) —
  // jump straight to the OTP step. There's no password in this case, so verifyOtp below
  // sends the user back to the login page afterwards instead of auto-logging in.
  const resumedEmail = location.state?.verifyEmail || '';
  const [step, setStep] = useState(resumedEmail ? 2 : 1); // 1=signup form, 2=otp verification
  const [form, setForm] = useState({ email: resumedEmail, password: '', confirmPassword: '' });
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const { show, NotificationBar } = useNotification();

  const handle = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async e => {
    e.preventDefault();
    const { email, password, confirmPassword } = form;
    if (!email || !password) { show(t('signup.fillRequiredFields'), 'error'); return; }
    if (password !== confirmPassword) { show(t('signup.passwordsMismatch'), 'error'); return; }
    if (password.length < 6) { show(t('signup.passwordMinLength'), 'error'); return; }

    setLoading(true);
    try {
      const res = await api.post('/auth/signup', { email, password });
      if (res.data.success) {
        show(t('signup.otpSent'), 'success');
        setStep(2);
      } else {
        show(res.data.message, 'error');
      }
    } catch (err) {
      const msg = err?.response?.data?.message || t('signup.signupFailed');
      show(msg, 'error');
    } finally { setLoading(false); }
  };

  const verifyOtp = async e => {
    e.preventDefault();
    if (!otp) { show(t('signup.enterOtp'), 'error'); return; }

    setLoading(true);
    try {
      const res = await api.post('/auth/signup/verify-otp', { email: form.email, otp });
      if (res.data.success) {
        if (!form.password) {
          // Resumed from a login attempt — we never captured the password, so just send
          // them back to sign in normally. The toast here would never be seen since this
          // component unmounts on navigate, so hand the message to the login page instead.
          navigate('/products/order-tracking/login', {
            state: { flashMessage: t('signup.emailVerifiedFlash'), flashType: 'success' }
          });
          return;
        }
        // Auto-login after verification so the profile gate can redirect them
        const loginRes = await api.post('/auth/login', { email: form.email, password: form.password });
        if (loginRes.data.success) {
          login(loginRes.data.token, loginRes.data.user);
          navigate('/products/order-tracking/profile');
        } else {
          navigate('/products/order-tracking/login', {
            state: { flashMessage: t('signup.emailVerifiedFlash'), flashType: 'success' }
          });
        }
      } else {
        show(res.data.message, 'error');
      }
    } catch (err) {
      const msg = err?.response?.data?.message || t('signup.verificationFailed');
      show(msg, 'error');
    } finally { setLoading(false); }
  };

  const resendOtp = async () => {
    setResending(true);
    try {
      const res = await api.post('/auth/signup/resend-otp', { email: form.email });
      show(res.data.success ? t('signup.otpResent') : res.data.message, res.data.success ? 'success' : 'error');
    } catch (err) {
      show(err?.response?.data?.message || t('signup.resendOtpFailed'), 'error');
    } finally { setResending(false); }
  };

  useEffect(() => {
    if (location.state?.flashMessage) {
      show(location.state.flashMessage, location.state.flashType || 'success');
      navigate(location.pathname, { replace: true, state: { verifyEmail: resumedEmail } });
    }
    // Resumed here from a failed login on an unverified account — the OTP from the
    // original signup may already be gone/expired, so get a fresh one right away.
    if (resumedEmail) resendOtp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="auth-page">
      <Helmet>
        <title>Create Free Account | EffyVenture Order Tracking</title>
        <meta name="description" content="Sign up free for EffyVenture Order Tracking. Manage orders, inventory, customers, and generate GST invoices — no credit card required." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://order.effyventure.com/products/order-tracking/signup" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://order.effyventure.com/products/order-tracking/signup" />
        <meta property="og:title" content="Create Free Account | EffyVenture Order Tracking" />
        <meta property="og:description" content="Sign up free for EffyVenture Order Tracking. Manage orders, inventory, customers, and generate GST invoices — no credit card required." />
      </Helmet>
      <NotificationBar />
      <div className="auth-split">
        <div className="auth-rail">
          <div className="auth-rail__content">
            <span className="eyebrow eyebrow--light">{t('signup.eyebrow')}</span>
            <h2 className="auth-rail__headline">{t('signup.headline')}</h2>
            <p className="auth-rail__sub">{t('signup.sub')}</p>
            <ul className="auth-rail__features">
              <li><span className="auth-rail__dot" />{t('signup.feature1')}</li>
              <li><span className="auth-rail__dot" />{t('signup.feature2')}</li>
              <li><span className="auth-rail__dot" />{t('signup.feature3')}</li>
            </ul>
          </div>
        </div>

        <div className="auth-card shine-rail">
          <div className="auth-card__brand">
            <BrandHomeLink className="auth-brand-logo">
              <img src="/logo/logo-icon.svg" alt="EffyVenture" className="auth-brand-logo__img" />
              <span className="auth-brand-logo__text">
                <span className="auth-brand-logo__effy">Effy</span><span className="auth-brand-logo__venture">Venture</span>
              </span>
            </BrandHomeLink>
            <span className="auth-card__product">{t('signup.productLabel')}</span>
          </div>
          {step === 1 ? (
            <>
              <h1 className="auth-card__title">{t('signup.title')}</h1>
              <p className="auth-card__subtitle">{t('signup.subtitle')}</p>
              <form onSubmit={submit}>
                <div className="form-group">
                  <label className="form-label">{t('signup.emailAddress')}</label>
                  <input
                    className="input-field"
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handle}
                    placeholder={t('signup.emailPlaceholder')}
                    autoFocus
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('signup.password')}</label>
                  <input
                    className="input-field"
                    type="password"
                    name="password"
                    value={form.password}
                    onChange={handle}
                    placeholder={t('signup.passwordPlaceholder')}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('signup.confirmPassword')}</label>
                  <input
                    className="input-field"
                    type="password"
                    name="confirmPassword"
                    value={form.confirmPassword}
                    onChange={handle}
                    placeholder={t('signup.confirmPasswordPlaceholder')}
                    required
                  />
                </div>
                <button type="submit" className="btn btn-primary auth-submit" disabled={loading}>
                  {loading ? <><span className="spinner" /> {t('signup.creatingAccount')}</> : t('signup.createAccount')}
                </button>
              </form>
              <GoogleAuthButton />
              <p className="auth-card__switch">
                {t('signup.alreadyHaveAccount')} <Link to="/products/order-tracking/login">{t('signup.signIn')}</Link>
              </p>
            </>
          ) : (
            <>
              <span className="eyebrow" style={{ marginBottom: 10 }}>{t('signup.verificationEyebrow')}</span>
              <h1 className="auth-card__title">{t('signup.verifyTitle')}</h1>
              <p className="auth-card__subtitle">{t('signup.verifySubtitle', { email: form.email })}</p>
              <form onSubmit={verifyOtp}>
                <div className="form-group">
                  <label className="form-label">{t('signup.otpCode')}</label>
                  <input
                    className="input-field"
                    value={otp}
                    onChange={e => setOtp(e.target.value)}
                    placeholder={t('signup.otpPlaceholder')}
                    maxLength={6}
                    autoFocus
                    required
                  />
                </div>
                <button type="submit" className="btn btn-primary auth-submit" disabled={loading}>
                  {loading ? <><span className="spinner" /> {t('signup.verifying')}</> : t('signup.verifyAndContinue')}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary auth-submit"
                  style={{ marginTop: 8 }}
                  onClick={resendOtp}
                  disabled={resending}
                >
                  {resending ? <><span className="spinner" /> {t('signup.resending')}</> : t('signup.resendOtp')}
                </button>
                {!resumedEmail && (
                  <button
                    type="button"
                    className="btn btn-secondary auth-submit"
                    style={{ marginTop: 8 }}
                    onClick={() => setStep(1)}
                  >
                    {t('signup.back')}
                  </button>
                )}
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
